/**
 * <newsletter-popup>
 *
 * Decides WHETHER and WHEN the newsletter modal opens, and remembers the answer
 * across visits. Rendered by sections/section-newsletter-popup.liquid.
 *
 * WHAT THIS FILE OWNS: the eligibility gate, the trigger, and the open/close
 * choreography. It owns none of the form — the markup wraps the existing
 * <newsletter-form> element, which does the submit, and this only listens for
 * the `newsletter:subscribed` event it dispatches on success.
 *
 * IT OWNS NO FOCUS TRAP, NO ESC HANDLER AND NO SCROLL LOCK EITHER. The markup is
 * a real <dialog> opened with showModal(), so the platform supplies the trap,
 * Esc, and inertness of the page behind; the `scroll-lock` attribute picks up the
 * sitewide `html:has(dialog[scroll-lock][open]) { overflow: hidden }` rule in
 * critical.css. Re-implementing any of that by hand would be strictly worse.
 *
 * 🚨 IT MUST NEVER OPEN OVER THE LOADING SCREEN. When a <loading-screen> is on
 * the page the trigger does not start arming until that element says it is done.
 * The wait is bounded — see LOADING_SAFETY_MS — because a splash that throws
 * must not take the popup down with it. Failing towards "show it late" is the
 * correct trade; failing towards "never show it" would be invisible in QA.
 *
 * STORAGE FAILS CLOSED, ON PURPOSE. Every read and write is wrapped, and a
 * browser that refuses storage (Safari private browsing throws on write) is
 * treated as a first-time visitor. The alternative — suppressing the popup when
 * we cannot remember a dismissal — would silently disable the feature for a
 * whole class of visitor. A repeat prompt is the lesser harm, and it is capped
 * anyway by the session key sharing the same fate.
 */
if (!customElements.get('newsletter-popup')) {
  customElements.define(
    'newsletter-popup',
    class NewsletterPopup extends HTMLElement {
      /* 🚨 MIRRORS LoadingScreen.DONE_EVENT in assets/loading-screen.js. These
         two strings are the only thing joining the splash to the popup. */
      static DONE_EVENT = 'dogwood:loading-screen:done';

      /* Longer than the splash's own MAX_TOTAL_MS ceiling (3600ms), so this only
         ever fires when the loading screen has genuinely failed to finish. */
      static LOADING_SAFETY_MS = 5000;

      /* 🚨 MIRRORS the panel's `duration-400` in the section markup. Too short
         and the card is torn from the DOM mid-fade. */
      static EXIT_MS = 400;

      /* How long the success message stays readable before the popup leaves. */
      static SUCCESS_HOLD_MS = 2200;

      static KEY_SUBSCRIBED = 'dogwood:popup:subscribed';
      static KEY_DISMISSED = 'dogwood:popup:dismissed-at';
      static KEY_SEEN = 'dogwood:popup:seen';

      static DAY_MS = 86400000;

      connectedCallback() {
        this.dialog = this.querySelector('[data-popup-dialog]');
        this.panel = this.querySelector('[data-popup-panel]');
        this.overlay = this.querySelector('[data-popup-overlay]');
        if (!this.dialog || !this.panel) return;

        this.designMode = this.hasAttribute('data-design-mode');
        this.isClosing = false;
        this.timers = [];

        this.handleDismissClick = this.handleDismissClick.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleSubscribed = this.handleSubscribed.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.checkScroll = this.checkScroll.bind(this);
        this.handleLoadingDone = this.handleLoadingDone.bind(this);

        this.dialog.addEventListener('click', this.handleDismissClick);
        /* `cancel` is Esc. Cancelling the platform's own close lets the fade run
           and lets the dismissal be recorded; without this the dialog vanishes
           instantly and the visitor is re-prompted on the next page. */
        this.dialog.addEventListener('cancel', this.handleCancel);
        this.addEventListener('newsletter:subscribed', this.handleSubscribed);

        if (!this.isEligible()) return;

        this.waitForLoadingScreen(() => this.armTrigger());
      }

      disconnectedCallback() {
        this.timers.forEach(clearTimeout);
        this.timers = [];
        this.teardownTrigger();
        this.dialog?.removeEventListener('click', this.handleDismissClick);
        this.dialog?.removeEventListener('cancel', this.handleCancel);
        this.removeEventListener('newsletter:subscribed', this.handleSubscribed);
        document.removeEventListener(NewsletterPopup.DONE_EVENT, this.handleLoadingDone);
      }

      after(delay, fn) {
        this.timers.push(setTimeout(fn, Math.max(0, delay)));
      }

      /* ---------------------------------------------------------------- gate */

      /* Reads never throw: a blocked or full store returns null, which reads as
         "no record", which reads as a first visit. */
      read(store, key) {
        try {
          return window[store].getItem(key);
        } catch {
          return null;
        }
      }

      write(store, key, value) {
        /* Previewing in the theme editor must not burn the merchant's own
           frequency window on the live storefront. */
        if (this.designMode) return;
        try {
          window[store].setItem(key, value);
        } catch {
          /* Storage refused. The popup will simply ask again next visit. */
        }
      }

      isEligible() {
        /* The editor ignores every gate — a merchant cannot style something they
           are not allowed to see. */
        if (this.designMode) return true;

        if (this.read('localStorage', NewsletterPopup.KEY_SUBSCRIBED)) return false;

        const dismissedAt = this.read('localStorage', NewsletterPopup.KEY_DISMISSED);

        switch (this.dataset.frequency) {
          case 'once':
            return !dismissedAt;

          case 'session':
            return !this.read('sessionStorage', NewsletterPopup.KEY_SEEN);

          case 'days':
          default: {
            if (!dismissedAt) return true;
            const days = parseInt(this.dataset.frequencyDays, 10) || 1;
            /* A clock moved backwards, or a corrupt value, produces a negative
               or NaN age. Treat anything that is not a real elapsed span as
               "long enough ago" rather than locking the popup out forever. */
            const age = Date.now() - Number(dismissedAt);
            return !(age >= 0 && age < days * NewsletterPopup.DAY_MS);
          }
        }
      }

      /* ------------------------------------------------------------- arming */

      waitForLoadingScreen(onReady) {
        this.onLoadingReady = onReady;

        if (!document.querySelector('loading-screen')) {
          onReady();
          return;
        }

        document.addEventListener(NewsletterPopup.DONE_EVENT, this.handleLoadingDone, {
          once: true,
        });
        this.after(NewsletterPopup.LOADING_SAFETY_MS, this.handleLoadingDone);
      }

      handleLoadingDone() {
        /* Reachable twice — once from the event, once from the safety timer. */
        if (!this.onLoadingReady) return;
        const run = this.onLoadingReady;
        this.onLoadingReady = null;
        run();
      }

      armTrigger() {
        if (this.dataset.triggerType === 'scroll') {
          this.armScrollTrigger();
        } else {
          const seconds = parseFloat(this.dataset.triggerSeconds) || 0;
          this.after(seconds * 1000, () => this.open());
        }
      }

      armScrollTrigger() {
        this.scrollTarget = (parseFloat(this.dataset.triggerScroll) || 0) / 100;

        /* A page shorter than the viewport can never reach any percentage, so a
           strict reading would mean the popup never appears on short pages —
           silently, and only on some templates. Treat "nothing to scroll" as the
           condition already being met. */
        if (this.scrollableHeight() <= 0) {
          this.open();
          return;
        }

        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.checkScroll();
      }

      teardownTrigger() {
        window.removeEventListener('scroll', this.handleScroll);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.ticking = false;
      }

      scrollableHeight() {
        return document.documentElement.scrollHeight - window.innerHeight;
      }

      handleScroll() {
        /* One read per frame. Scroll fires far more often than the screen
           refreshes and every extra measurement is wasted work. */
        if (this.ticking) return;
        this.ticking = true;
        this.frame = requestAnimationFrame(this.checkScroll);
      }

      checkScroll() {
        this.ticking = false;

        const scrollable = this.scrollableHeight();
        if (scrollable <= 0) {
          this.open();
          return;
        }

        if (window.scrollY / scrollable >= this.scrollTarget) this.open();
      }

      /* ----------------------------------------------------------- open/close */

      open() {
        if (this.dialog.open || this.isClosing) return;

        this.teardownTrigger();
        this.write('sessionStorage', NewsletterPopup.KEY_SEEN, '1');

        this.dialog.showModal();

        /* Two frames, not one. The first lets the dialog leave display:none and
           be laid out; the second guarantees the browser has taken a style
           snapshot with the card still at opacity 0, so the transition has
           something to animate FROM. With a single frame the card can appear
           fully formed and the fade is simply never seen. */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!this.dialog.open) return;
            this.panel.dataset.open = '';
            if (this.overlay) this.overlay.dataset.open = '';
          });
        });
      }

      handleDismissClick(event) {
        /* Either the "no thanks" button, or a click that landed on the backdrop
           rather than on the card. The card is a positioned child, so clicks on
           it never reach the dialog or the overlay. */
        if (
          event.target.closest('[data-popup-dismiss]') ||
          event.target === this.dialog ||
          event.target === this.overlay
        ) {
          this.dismiss();
        }
      }

      handleCancel(event) {
        event.preventDefault();
        this.dismiss();
      }

      dismiss() {
        this.write('localStorage', NewsletterPopup.KEY_DISMISSED, String(Date.now()));
        this.close();
      }

      handleSubscribed() {
        /* Signing up is a permanent answer — the frequency setting governs people
           who said no, not people who already said yes. */
        this.write('localStorage', NewsletterPopup.KEY_SUBSCRIBED, '1');
        this.after(NewsletterPopup.SUCCESS_HOLD_MS, () => this.close());
      }

      close() {
        if (!this.dialog.open || this.isClosing) return;
        this.isClosing = true;

        delete this.panel.dataset.open;
        if (this.overlay) delete this.overlay.dataset.open;

        this.after(NewsletterPopup.EXIT_MS, () => {
          this.dialog.close();
          this.isClosing = false;
        });
      }
    },
  );
}
